import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GameResult {
  id: string;
  game_date: string;
  home_team_id: number | null;
  away_team_id: number | null;
  home_team_name: string;
  away_team_name: string;
  home_score: number;
  away_score: number;
  winner_team_name: string;
  loser_team_name: string;
  venue_name: string | null;
  sport_name: string | null;
  is_home_winner: boolean;
}

interface UseRecentGameResultsOptions {
  stateProvince: string;
  country?: string;
  sportFilter?: string;
  daysBack?: number;
  limit?: number;
}

export const useRecentGameResults = (options: UseRecentGameResultsOptions) => {
  const { stateProvince, country = 'United States', sportFilter, daysBack = 7, limit = 20 } = options;
  const [results, setResults] = useState<GameResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecentResults = async () => {
      try {
        setLoading(true);
        setError(null);

        const today = new Date();
        const pastDate = new Date(today);
        pastDate.setDate(pastDate.getDate() - daysBack);
        const pastDateStr = pastDate.toISOString().split('T')[0];
        const todayStr = today.toISOString().split('T')[0];

        // Fetch completed games
        const { data: gamesData, error: gamesError } = await supabase
          .from('games')
          .select(`
            id,
            game_date,
            home_team_id,
            away_team_id,
            venue_id,
            sport_id
          `)
          .eq('game_status', 'completed')
          .gte('game_date', pastDateStr)
          .lte('game_date', todayStr)
          .order('game_date', { ascending: false })
          .limit(limit);

        if (gamesError) throw gamesError;

        if (!gamesData || gamesData.length === 0) {
          setResults([]);
          setLoading(false);
          return;
        }

        // Fetch game results
        const gameIds = gamesData.map(g => g.id);
        const { data: resultsData } = await supabase
          .from('game_results')
          .select('game_id, home_team_score, away_team_score, winning_team')
          .in('game_id', gameIds);

        const resultMap = new Map(resultsData?.map(r => [r.game_id, r]) || []);

        // Filter to only games with results
        const gamesWithResults = gamesData.filter(g => resultMap.has(g.id));

        if (gamesWithResults.length === 0) {
          setResults([]);
          setLoading(false);
          return;
        }

        // Get unique team IDs
        const teamIds = [...new Set([
          ...gamesWithResults.map(g => g.home_team_id).filter(Boolean),
          ...gamesWithResults.map(g => g.away_team_id).filter(Boolean)
        ])] as number[];

        // Fetch teams
        const { data: teamsData } = await supabase
          .from('teams')
          .select('team_id, team_name, state_province')
          .in('team_id', teamIds);

        const teamMap = new Map(teamsData?.map(t => [t.team_id, t]) || []);

        // Fetch venues
        const venueIds = gamesWithResults.map(g => g.venue_id).filter(Boolean) as string[];
        let venueMap = new Map<string, any>();
        if (venueIds.length > 0) {
          const { data: venuesData } = await supabase
            .from('venues')
            .select('venue_id, venue_name, state_province')
            .in('venue_id', venueIds);
          venueMap = new Map(venuesData?.map(v => [v.venue_id, v]) || []);
        }

        // Fetch sports for names
        const sportIds = [...new Set(gamesWithResults.map(g => g.sport_id).filter(Boolean))] as number[];
        let sportMap = new Map<number, string>();
        if (sportIds.length > 0) {
          const { data: sportsData } = await supabase
            .from('available_sports')
            .select('id, sport_name')
            .in('id', sportIds);
          sportMap = new Map(sportsData?.map(s => [s.id, s.sport_name]) || []);
        }

        // Build enriched results
        const enrichedResults: GameResult[] = gamesWithResults
          .map(game => {
            const homeTeam = teamMap.get(game.home_team_id!);
            const awayTeam = teamMap.get(game.away_team_id!);
            const venue = venueMap.get(game.venue_id || '');
            const result = resultMap.get(game.id)!;

            const isHomeWinner = (result.home_team_score || 0) > (result.away_team_score || 0);
            const gameState = venue?.state_province || homeTeam?.state_province || awayTeam?.state_province;

            return {
              id: game.id,
              game_date: game.game_date,
              home_team_id: game.home_team_id,
              away_team_id: game.away_team_id,
              home_team_name: homeTeam?.team_name || 'Unknown',
              away_team_name: awayTeam?.team_name || 'Unknown',
              home_score: result.home_team_score || 0,
              away_score: result.away_team_score || 0,
              winner_team_name: isHomeWinner 
                ? (homeTeam?.team_name || 'Unknown') 
                : (awayTeam?.team_name || 'Unknown'),
              loser_team_name: isHomeWinner 
                ? (awayTeam?.team_name || 'Unknown') 
                : (homeTeam?.team_name || 'Unknown'),
              venue_name: venue?.venue_name || null,
              sport_name: game.sport_id ? sportMap.get(game.sport_id) || null : null,
              is_home_winner: isHomeWinner,
              _state: gameState,
            };
          })
          .filter((r): r is GameResult & { _state: string } => 
            r !== null && (!stateProvince || (r as any)._state === stateProvince)
          )
          .map(({ _state, ...rest }: any) => rest as GameResult);

        setResults(enrichedResults);
      } catch (err) {
        console.error('Error fetching recent results:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch results');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentResults();
  }, [stateProvince, country, sportFilter, daysBack, limit]);

  return {
    results,
    loading,
    error,
  };
};
